'use client';

import React, { useEffect, useRef, useState, useCallback } from 'react';
import Script from 'next/script';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface GoogleSignInButtonProps {
  onSuccess: (idToken: string) => void;
  onFailure: (error: string) => void;
  text?: string;
  theme?: 'outline' | 'filled_blue';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
}

declare global {
  interface Window {
    google?: any;
  }
}

const GoogleLogo = ({ className = "w-5 h-5 mr-3 shrink-0" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
    />
  </svg>
);

export default function GoogleSignInButton({
  onSuccess,
  onFailure,
  text = 'Sign in with Google',
  theme = 'filled_blue',
  shape = 'pill',
}: GoogleSignInButtonProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const isInitializedRef = useRef(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [hasError, setHasError] = useState(false);

  const renderGoogleButton = useCallback(() => {
    if (!containerRef.current || !window.google) return;
    containerRef.current.innerHTML = '';
    
    const parentWidth = wrapperRef.current?.clientWidth || containerRef.current.clientWidth || 380;
    const targetWidth = Math.min(Math.max(parentWidth, 200), 400);

    window.google.accounts.id.renderButton(containerRef.current, {
      theme: theme,
      size: 'large',
      text: text.toLowerCase().includes('sign up') ? 'signup_with' : 'signin_with',
      shape: shape,
      width: targetWidth,
      logo_alignment: 'left',
      locale: 'en',
    });
  }, [theme, shape, text]);

  const initializeGoogle = useCallback(() => {
    try {
      if (!window.google) {
        setHasError(true);
        onFailure('Google SDK failed to load.');
        setIsInitializing(false);
        return;
      }

      if (!isInitializedRef.current) {
        const clientId = (process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID || '844417757492-o76fl70t2cg114q8lv9gqvn859lqg9sp.apps.googleusercontent.com').replace(/"/g, '');

        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: (response: any) => {
            if (response.credential) {
              onSuccess(response.credential);
            } else {
              onFailure('Failed to retrieve Google credentials.');
            }
          },
        });
        isInitializedRef.current = true;
      }

      renderGoogleButton();
      setIsInitializing(false);
    } catch (error: any) {
      console.error('Error initializing Google Sign-In:', error);
      setHasError(true);
      onFailure(error.message || 'Error initializing Google Sign-In');
      setIsInitializing(false);
    }
  }, [onSuccess, onFailure, renderGoogleButton]);

  useEffect(() => {
    if (scriptLoaded && window.google) {
      initializeGoogle();
    }
  }, [scriptLoaded, initializeGoogle]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (scriptLoaded && window.google && isInitializedRef.current && containerRef.current) {
          renderGoogleButton();
        }
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [scriptLoaded, renderGoogleButton]);

  const handleManualClick = () => {
    if (window.google?.accounts?.id) {
      try {
        window.google.accounts.id.prompt();
      } catch (e) {
        console.error('Google prompt error:', e);
      }
    } else {
      onFailure('Google Sign-In is not ready. Please try again.');
    }
  };

  return (
    <div ref={wrapperRef} className="w-full flex flex-col items-center">
      <Script
        src="https://accounts.google.com/gsi/client"
        strategy="afterInteractive"
        onLoad={() => setScriptLoaded(true)}
        onError={() => {
          setHasError(true);
          onFailure('Failed to load Google script.');
          setIsInitializing(false);
        }}
      />

      {isInitializing && (
        <Button
          type="button"
          disabled
          className="w-full h-12 bg-[#1a73e8] hover:bg-[#1557b0] text-white font-semibold text-base shadow-md rounded-[40px] flex items-center justify-center border-none transition-all px-5"
        >
          <div className="bg-white p-1.5 rounded-full mr-3 flex items-center justify-center shrink-0 shadow-sm">
            <GoogleLogo className="w-4 h-4" />
          </div>
          <span>{text}</span>
          <Loader2 className="ml-3 h-4 w-4 animate-spin text-white/80" />
        </Button>
      )}

      {hasError && !isInitializing && (
        <Button
          type="button"
          onClick={handleManualClick}
          className="w-full h-12 bg-[#1a73e8] hover:bg-[#1557b0] text-white font-semibold text-base shadow-md rounded-[40px] flex items-center justify-center border-none transition-all px-5"
        >
          <div className="bg-white p-1.5 rounded-full mr-3 flex items-center justify-center shrink-0 shadow-sm">
            <GoogleLogo className="w-4 h-4" />
          </div>
          <span>{text}</span>
        </Button>
      )}

      <div
        ref={containerRef}
        id="google-signin-button"
        className={`w-full min-h-[44px] flex items-center justify-center transition-opacity duration-300 ${
          isInitializing || hasError ? 'hidden opacity-0' : 'block opacity-100'
        }`}
      />
    </div>
  );
}


